import { Group, Stack, Text, Title } from "@mantine/core";
import { useSyncExternalStore, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { subscribeToLocationSearchChanges } from "../../shared/url/locationSearch.js";

export type AdministrationRole = "admin" | "super_admin";

export function PrimaryNavigation({
  hasReportAccess,
  onNavigate,
  orientation = "horizontal",
  role,
}: {
  readonly hasReportAccess: boolean;
  readonly onNavigate?: () => void;
  readonly orientation?: "horizontal" | "vertical";
  readonly role: "admin" | "super_admin" | "viewer";
}) {
  useLocation();
  useSyncExternalStore(
    subscribeToLocationSearchChanges,
    () => `${window.location.search}\n${window.location.hash}`,
    () => "\n",
  );
  const customerDestination = (pathname: "/" | "/saved-views") => ({
    hash: window.location.hash,
    pathname,
    search: window.location.search,
  });
  return (
    <Group
      aria-label="Primary"
      className={`app-primary-nav app-primary-nav--${orientation}`}
      component="nav"
      gap={orientation === "vertical" ? "xs" : "sm"}
      wrap="wrap"
    >
      {hasReportAccess ? (
        <>
          <NavLink end onClick={onNavigate} to={customerDestination("/")}>Report</NavLink>
          <NavLink onClick={onNavigate} to={customerDestination("/saved-views")}>Saved Views</NavLink>
        </>
      ) : null}
      {role === "viewer" ? null : (
        <NavLink onClick={onNavigate} to="/admin">Administration</NavLink>
      )}
    </Group>
  );
}

export function AdministrationFrame({
  children,
  role,
}: {
  readonly children: ReactNode;
  readonly role: AdministrationRole;
}) {
  return (
    <Stack className="admin-shell" gap="lg">
      <div>
        <Title order={2}>Administration</Title>
        <Text c="dimmed" size="sm">
          {role === "super_admin"
            ? "You can manage all brands and system settings."
            : "You only see and manage people and access within brands and accounts you can administer."}
        </Text>
      </div>
      <Group aria-label="Administration sections" className="admin-navigation" component="nav" gap="md" wrap="wrap">
        <NavLink to="/admin/users">People and access</NavLink>
        <NavLink to="/admin/subgroups">Subgroups</NavLink>
        {role === "super_admin" ? (
          <>
            <NavLink to="/admin/brands">Brands and mappings</NavLink>
            <NavLink to="/admin/domains">Email domains</NavLink>
          </>
        ) : null}
      </Group>
      {children}
    </Stack>
  );
}

export function AdministrationLanding() {
  return <Text>Choose an administration area.</Text>;
}
